import { CATEGORY_LABELS, CATEGORY_ORDER, EXPENSE_GROUP_LABELS, expenseGroupOf, visibleCategory, type ExpenseGroupKey } from './finance';
import type { IngredientField, RecipeField } from './importCatalog';
import { parseSiNo, parseUnit } from './importCatalog';
import { parseExpenseCategory } from './importExpenses';
import { normalizeText, parseImportNumber } from './importer';
import { adivinarCategoria } from './receipts';
import type { ExpenseCategory, TenantSettings } from './types';
import type { Unit } from './units';

/**
 * Las plantillas de Excel de Alex (oct 2026): ingredientes, recetas y gastos.
 *
 * Son las que se descargan desde la app (`web/public/plantillas`) y tienen un
 * formato FIJO: título en la fila 1, instrucciones en la 2, cabecera en la 4,
 * una fila de ayuda debajo, ejemplos en gris que empiezan por «Ejemplo:» y,
 * en recetas y gastos, una línea verde «Escribe tus … debajo de esta línea ↓».
 * Quien la rellena no tiene que emparejar columnas ni contestar nada: se
 * reconoce por la cabecera y se lee con un lector fijo.
 *
 * Cualquier otro Excel sigue por el camino de siempre (cabeceras por nombre,
 * mapeo de columnas y mapeo guardado): esto solo se activa si la cabecera es
 * la de la plantilla.
 *
 * Todo lo de aquí trabaja con las filas CRUDAS de la hoja, sin quitar las
 * vacías: `raw[i]` es la fila i+1 del Excel. Así los avisos dicen la fila de
 * verdad («fila 18»), que es la que el usuario ve en su hoja.
 */

export type PlantillaKind = 'ingredientes' | 'recetas' | 'gastos';

/** Nombres de archivo: los mismos de Alex, para que se descarguen tal cual. */
export const PLANTILLA_ARCHIVOS: Record<PlantillaKind, string> = {
  ingredientes: 'Plantilla-ingredientes-Alexia.xlsx',
  recetas: 'Plantilla-recetas-Alexia.xlsx',
  gastos: 'Plantilla-gastos-Alexia.xlsx',
};

/** Hoja de cada plantilla. */
export const PLANTILLA_HOJAS: Record<PlantillaKind, string> = {
  ingredientes: 'Ingredientes',
  recetas: 'Recetas',
  gastos: 'Gastos',
};

/** Cabeceras de la plantilla, en orden (sin el «*» de obligatoria). */
export const PLANTILLA_CABECERAS: Record<PlantillaKind, string[]> = {
  ingredientes: ['Ingrediente', 'Lo compras en', 'Cuánto trae', 'En qué se mide', 'Precio', '¿Con IVA?', 'Proveedor', 'Grupo', 'Marca'],
  recetas: [
    'Plato o preparación', '¿Lo vendes tal cual?', 'Salen / rinde', 'Unidad', 'Precio de venta', 'Grupo de tu carta',
    'Ingrediente o preparación', 'Cantidad', 'Unidad', 'Se tira al limpiar (%)', '¿Es empaque?',
  ],
  gastos: [
    'Fecha', 'Proveedor', 'Total', 'IVA', 'Categoría', '¿Ya lo pagaste?', 'Vence el', 'Nº de factura', 'Concepto',
    '¿Qué te dieron?', 'Local',
  ],
};

/** Principio de la fila de ayuda de cada plantilla (la que va debajo de la cabecera). */
const AYUDA: Record<PlantillaKind, string> = {
  ingredientes: 'como lo llamas',
  recetas: 'el nombre solo en su primera fila',
  gastos: 'dia de la factura',
};

const CABECERAS_NORMALIZADAS = Object.fromEntries(
  (Object.keys(PLANTILLA_CABECERAS) as PlantillaKind[]).map((k) => [k, PLANTILLA_CABECERAS[k].map((h) => normalizeText(h))]),
) as Record<PlantillaKind, string[]>;

export interface PlantillaDetectada {
  kind: PlantillaKind;
  /** Índice (0) de la fila de cabecera en las filas crudas. */
  cabecera: number;
}

/**
 * ¿Es una de nuestras plantillas? Se mira la cabecera en las primeras filas:
 * la primera columna y al menos dos de las tres siguientes tienen que decir lo
 * de la plantilla (sin importar mayúsculas, acentos, espacios ni el «*»).
 * Basta para no confundirla con el Excel de nadie y deja que alguien añada
 * columnas al final.
 */
export function detectarPlantilla(raw: string[][]): PlantillaDetectada | undefined {
  for (let i = 0; i < Math.min(raw.length, 15); i += 1) {
    const fila = (raw[i] ?? []).map((c) => normalizeText(String(c ?? '')));
    for (const kind of Object.keys(CABECERAS_NORMALIZADAS) as PlantillaKind[]) {
      const esperadas = CABECERAS_NORMALIZADAS[kind];
      // La primera, exacta; de las otras tres, basta con dos (una cabecera
      // retocada a mano no tiene que tumbar la plantilla entera).
      const iguales = esperadas.slice(0, 4).filter((h, j) => fila[j] === h).length;
      if (fila[0] === esperadas[0] && iguales >= 3) return { kind, cabecera: i };
    }
  }
  return undefined;
}

const vacia = (fila: string[] | undefined): boolean => !fila || fila.every((c) => String(c ?? '').trim() === '');
const empiezaPor = (celda: string | undefined, prefijo: string): boolean => normalizeText(String(celda ?? '')).startsWith(prefijo);
const esEjemplo = (celda: string | undefined): boolean => empiezaPor(celda, 'ejemplo');

/**
 * Las filas que traen datos del usuario, con su número de fila del Excel.
 *
 * Fuera: la cabecera y lo de arriba, la fila de ayuda, la línea «Escribe tus
 * … debajo de esta línea» y todo lo que hay por encima de ella (la zona de
 * ejemplos), las filas vacías y las que empiezan por «Ejemplo:» (`colEjemplo`:
 * en gastos el «Ejemplo:» va en el proveedor). Sin la línea —la de
 * ingredientes no la tiene, o alguien la borró— se leen todas las de debajo
 * de la cabecera, menos la ayuda y los ejemplos.
 */
export function filasDePlantilla(raw: string[][], det: PlantillaDetectada, colEjemplo = 0): Array<{ row: number; cells: string[] }> {
  let desde = det.cabecera + 1;
  if (empiezaPor(raw[desde]?.[0], AYUDA[det.kind])) desde += 1;
  for (let i = desde; i < raw.length; i += 1) {
    if (empiezaPor(raw[i]?.[0], 'escribe tus')) {
      desde = i + 1;
      break;
    }
  }
  const out: Array<{ row: number; cells: string[] }> = [];
  for (let i = desde; i < raw.length; i += 1) {
    const cells = (raw[i] ?? []).map((c) => String(c ?? '').trim());
    if (vacia(cells) || esEjemplo(cells[colEjemplo])) continue;
    out.push({ row: i + 1, cells });
  }
  return out;
}

const redondea = (n: number, d = 4): number => Math.round(n * 10 ** d) / 10 ** d;

/*
 * Galones y onzas líquidas: desde oct 2026 son unidades de la app (`gal`,
 * `floz`) y entran tal cual, sin convertir a L/mL. `parseUnit` las reconoce.
 */

/* ------------------------------------------------------------------ */
/* Ingredientes                                                        */
/* ------------------------------------------------------------------ */

/** Columnas en las que se entrega la plantilla de ingredientes a `buildIngredientImport`. */
export const PLANTILLA_INGREDIENTES_COLS: IngredientField[] = [
  'name', 'presentation', 'purchaseQty', 'purchaseUnit', 'netPrice', 'grossPrice', 'vatPct', 'supplier', 'group', 'brand',
];

export interface PlantillaLeida<F extends string> {
  /**
   * Filas para el importador de siempre: `datos[i]` es la fila i+2 del Excel
   * (el importador numera así), con las que no son datos en blanco.
   */
  datos: string[][];
  cols: F[];
  /** Filas con datos del usuario. */
  filas: number;
}

/**
 * Plantilla de ingredientes → filas del importador de siempre. «¿Con IVA?» =
 * Sí convierte el precio en «precio con IVA» con el IVA que se diga (el de la
 * cuenta, o el que se elija en «Antes de guardar»), y el importador saca el
 * neto. Sin «Cuánto trae», el formato es 1.
 */
export function leerPlantillaIngredientes(
  raw: string[][],
  det: PlantillaDetectada,
  ivaConIva: number,
): PlantillaLeida<IngredientField> & { conIva: number } {
  const datos: string[][] = Array.from({ length: Math.max(0, raw.length - 1) }, () => []);
  let conIva = 0;
  const filas = filasDePlantilla(raw, det);
  for (const { row, cells } of filas) {
    const [nombre = '', presentacion = '', cuanto = '', unidad = '', precio = '', iva = '', proveedor = '', grupo = '', marca = ''] = cells;
    const esConIva = parseSiNo(iva) === true && precio !== '';
    if (esConIva) conIva += 1;
    datos[row - 2] = [
      nombre, presentacion, cuanto, unidad,
      esConIva ? '' : precio,
      esConIva ? precio : '',
      esConIva ? String(ivaConIva) : '',
      proveedor, grupo, marca,
    ];
  }
  return { datos, cols: PLANTILLA_INGREDIENTES_COLS, filas: filas.length, conIva };
}

/* ------------------------------------------------------------------ */
/* Recetas                                                             */
/* ------------------------------------------------------------------ */

export const PLANTILLA_RECETAS_COLS: RecipeField[] = [
  'recipeName', 'kind', 'yieldQty', 'yieldUnit', 'portions', 'salePrice', 'group', 'componentName', 'qty', 'unit', 'wastePct', 'block',
];

const PORCIONES = new Set(['porcion', 'porciones', 'racion', 'raciones', 'porc']);

/**
 * Plantilla de recetas → filas del importador de siempre.
 *
 *  - El nombre va solo en la primera fila: las de abajo sin nombre son de la
 *    misma receta (se rellena hacia abajo). Repetirlo en cada fila también vale.
 *  - Un bloque que empieza por «Ejemplo:» no se importa, con todas sus filas.
 *  - «Salen / rinde» + «Unidad»: en un plato, porciones (casi siempre 1); en
 *    una preparación, cuánto rinde (400 piezas, 2 L).
 *  - «¿Es empaque?» = Sí → la línea va al bloque de empaque, aparte.
 *  - «Se tira al limpiar (%)» → la merma; 0,12 (celda con formato %) es 12.
 */
export function leerPlantillaRecetas(raw: string[][], det: PlantillaDetectada): PlantillaLeida<RecipeField> {
  const datos: string[][] = Array.from({ length: Math.max(0, raw.length - 1) }, () => []);
  // Aquí no se filtran los ejemplos fila a fila: un ejemplo es un BLOQUE.
  let desde = det.cabecera + 1;
  if (empiezaPor(raw[desde]?.[0], AYUDA.recetas)) desde += 1;
  for (let i = desde; i < raw.length; i += 1) {
    if (empiezaPor(raw[i]?.[0], 'escribe tus')) { desde = i + 1; break; }
  }
  let receta = '';
  let enEjemplo = false;
  let filas = 0;
  for (let i = desde; i < raw.length; i += 1) {
    const cells = (raw[i] ?? []).map((c) => String(c ?? '').trim());
    if (vacia(cells)) continue;
    const [nombre = '', tipo = '', salen = '', unidadSalen = '', precio = '', grupo = '', componente = '', cantidad = '', unidad = '', merma = '', empaque = ''] = cells;
    if (nombre !== '') {
      enEjemplo = esEjemplo(nombre);
      receta = nombre;
    }
    if (enEjemplo) continue;
    filas += 1;

    let yieldQty = '';
    let yieldUnit = '';
    let portions = '';
    if (nombre !== '') {
      const esPreparacion = /^(prepara|sub)/.test(normalizeText(tipo));
      const n = parseImportNumber(salen);
      const enPorciones = unidadSalen === '' || PORCIONES.has(normalizeText(unidadSalen));
      const unit: Unit | undefined = parseUnit(unidadSalen);
      if (!esPreparacion && (enPorciones || unit === 'pz')) {
        // Plato: «salen 4 porciones» es una receta de 4 porciones.
        const porciones = String(Math.max(1, Math.round(n ?? 1)));
        [yieldQty, yieldUnit, portions] = [porciones, 'pz', porciones];
      } else {
        yieldQty = n == null ? salen : String(redondea(n));
        yieldUnit = enPorciones ? 'pz' : unit ?? unidadSalen;
        portions = '1';
      }
    }
    const mermaNum = parseImportNumber(merma);
    const wastePct = mermaNum != null && mermaNum > 0 && mermaNum < 1 ? String(redondea(mermaNum * 100, 2)) : merma;
    datos[i - 1] = [
      receta,
      nombre !== '' ? tipo : '',
      yieldQty, yieldUnit, portions,
      nombre !== '' ? precio : '',
      nombre !== '' ? grupo : '',
      componente, cantidad, unidad, wastePct,
      parseSiNo(empaque) === true ? 'empaque' : '',
    ];
  }
  return { datos, cols: PLANTILLA_RECETAS_COLS, filas };
}

/* ------------------------------------------------------------------ */
/* Gastos: categorías                                                  */
/* ------------------------------------------------------------------ */

/** Nombre del grupo tal y como lo escribe la plantilla. */
const GRUPO_PLANTILLA: Record<ExpenseGroupKey, string> = {
  materia_prima: 'Materia prima',
  personal: 'Personal',
  generales: 'Gastos generales',
};
const FINANCIEROS = 'Financieros';

/**
 * Las categorías de la hoja «Categorías» de la plantilla. Desde oct 2026 son
 * las MISMAS de la app (`CATEGORY_ORDER`, decisión de Pol): «Grupo › Nombre».
 */
export const CATEGORIAS_PLANTILLA: Array<{ label: string; category: ExpenseCategory }> = CATEGORY_ORDER.map((category) => {
  const grupo = expenseGroupOf(category);
  return { label: `${grupo ? GRUPO_PLANTILLA[grupo] : FINANCIEROS} › ${CATEGORY_LABELS[category]}`, category };
});

const GRUPO_POR_TEXTO = new Map<string, ExpenseGroupKey | 'financieros'>([
  ['materia prima', 'materia_prima'],
  ['gastos de materia prima', 'materia_prima'],
  ['personal', 'personal'],
  ['gastos de personal', 'personal'],
  ['gastos generales', 'generales'],
  ['generales', 'generales'],
  ['financieros', 'financieros'],
]);

/** Categoría «normal» de un grupo, para los subgrupos propios de la cuenta. */
const CATEGORIA_DEL_GRUPO: Record<ExpenseGroupKey, ExpenseCategory> = {
  materia_prima: 'compras_materia_prima',
  personal: 'sueldos_salarios',
  generales: 'otros_gastos',
};

const POR_ETIQUETA = new Map(CATEGORIAS_PLANTILLA.map((c) => [normalizeText(c.label), c]));
const POR_NOMBRE = new Map(CATEGORIAS_PLANTILLA.map((c) => [normalizeText(CATEGORY_LABELS[c.category]), c]));

/** Cómo se enseña una categoría con las palabras de la plantilla: «Materia prima › Bebidas». */
export function etiquetaDeCategoria(category: ExpenseCategory, subgroup?: string): string {
  const visible = visibleCategory(category);
  const grupo = expenseGroupOf(visible);
  const base = `${grupo ? GRUPO_PLANTILLA[grupo] : FINANCIEROS} › ${CATEGORY_LABELS[visible]}`;
  return subgroup ? `${base} › ${subgroup}` : base;
}

export interface CategoriaLeida {
  category: ExpenseCategory;
  subgroup?: string;
  /** Lo que se enseña: «Materia prima › Comida». */
  label: string;
  /** true = la celda venía vacía y la puso Alexia. */
  deducida: boolean;
}

/**
 * La categoría de una fila de la plantilla.
 *
 *  - «Grupo › Subcategoría» de la lista de la plantilla.
 *  - «Grupo › X» con X un subgrupo propio de la cuenta (Configuración): la
 *    categoría normal del grupo —o la que se deduzca, si es del mismo grupo—
 *    con ese subgrupo.
 *  - Solo la subcategoría («Comida»), o el nombre de una categoría de la app
 *    («Alquiler»): también vale.
 *  - Vacía: la deduce Alexia como el escáner (lo que ya hiciste con ese
 *    proveedor, su nombre, el concepto) y se marca como deducida.
 *  - Cualquier otra cosa: undefined, y la fila se pregunta.
 */
export function leerCategoriaPlantilla(
  raw: string,
  contexto: { proveedor?: string; concepto?: string; subgrupos?: TenantSettings['expenseSubgroups']; historico?: Map<string, ExpenseCategory> },
): CategoriaLeida | undefined {
  const leida = (category: ExpenseCategory, subgroup?: string): CategoriaLeida => ({
    category, ...(subgroup ? { subgroup } : {}), label: subgroup ? `${etiquetaDeCategoria(category).split(' › ')[0]} › ${subgroup}` : etiquetaDeCategoria(category), deducida: false,
  });

  if (raw.trim() === '') {
    const category = deducirCategoria(contexto.proveedor, contexto.concepto, contexto.historico);
    return { category, label: etiquetaDeCategoria(category), deducida: true };
  }

  const exacta = POR_ETIQUETA.get(normalizeText(raw));
  if (exacta) return leida(exacta.category);

  const [grupoRaw, subRaw] = raw.includes('›') ? raw.split('›') : raw.includes('>') ? raw.split('>') : [undefined, raw];
  const sub = normalizeText(subRaw ?? '');
  const grupo = grupoRaw != null ? GRUPO_POR_TEXTO.get(normalizeText(grupoRaw)) : undefined;
  const propioDe = (g: ExpenseGroupKey) => (contexto.subgrupos?.[g] ?? []).find((s) => normalizeText(s) === sub);

  // Un subgrupo propio de la cuenta (Configuración), dentro de su grupo: la
  // categoría que se deduzca si es de ese grupo, o la normal del grupo.
  if (grupo && grupo !== 'financieros') {
    const propio = propioDe(grupo);
    if (propio) {
      // El nombre del subgrupo manda si dice una categoría de su grupo:
      // «Comisiones de plataformas» es «Comisiones (tarjeta y apps)», no
      // «Otros» (revisión del 14 oct 2026).
      const porSuNombre = parseExpenseCategory(propio);
      if (porSuNombre && expenseGroupOf(porSuNombre) === grupo) return leida(porSuNombre, propio);
      const deducida = deducirCategoria(contexto.proveedor, contexto.concepto, contexto.historico);
      return leida(expenseGroupOf(deducida) === grupo ? deducida : CATEGORIA_DEL_GRUPO[grupo], propio);
    }
  }
  // Solo el nombre de la categoría («Comida»), con su grupo bien o sin grupo.
  const porNombre = POR_NOMBRE.get(sub);
  if (porNombre && (grupo == null || (grupo === 'financieros' ? !expenseGroupOf(porNombre.category) : expenseGroupOf(porNombre.category) === grupo))) {
    return leida(porNombre.category);
  }
  // «Gastos generales › Comisiones de plataformas»: un nombre de antes (o
  // con otras palabras) que dice una categoría de ese mismo grupo.
  if (grupo && subRaw != null) {
    const parecida = parseExpenseCategory(subRaw);
    if (parecida && CATEGORY_ORDER.includes(parecida) && (grupo === 'financieros' ? !expenseGroupOf(parecida) : expenseGroupOf(parecida) === grupo)) {
      return leida(parecida);
    }
  }
  if (grupo == null) {
    for (const g of Object.keys(EXPENSE_GROUP_LABELS) as ExpenseGroupKey[]) {
      const propio = propioDe(g);
      if (propio) {
        const porSuNombre = parseExpenseCategory(propio);
        return leida(porSuNombre && expenseGroupOf(porSuNombre) === g ? porSuNombre : CATEGORIA_DEL_GRUPO[g], propio);
      }
    }
    // Un nombre de categoría de antes («Alquiler», «Compras materia prima»).
    const vieja = parseExpenseCategory(raw);
    if (vieja && normalizeText(raw).split(' ').length <= 4 && CATEGORY_ORDER.includes(vieja)) return leida(vieja);
  }
  return undefined;
}

/**
 * Lo que haría el escáner con esta fila: primero lo que el restaurante ya
 * hizo con ese proveedor, luego las palabras del concepto («Renta octubre»,
 * «Nómina») y el nombre del proveedor. Sin pistas, materia prima, que es lo
 * más probable en un restaurante.
 */
export function deducirCategoria(proveedor?: string, concepto?: string, historico?: Map<string, ExpenseCategory>): ExpenseCategory {
  const nombre = normalizeText(proveedor ?? '');
  if (nombre && historico?.has(nombre)) return visibleCategory(historico.get(nombre)!);
  const porConcepto = parseExpenseCategory(concepto);
  if (porConcepto) return porConcepto;
  const porEscaner = adivinarCategoria({ vendorName: proveedor ? { value: proveedor } : undefined, rawText: concepto ?? '' });
  if (porEscaner.categoria !== 'compras_materia_prima') return visibleCategory(porEscaner.categoria);
  return parseExpenseCategory(proveedor) ?? 'compras_materia_prima';
}

/*
 * La lectura de la plantilla de GASTOS vive en importGastos.ts desde el 14 oct
 * 2026 (bloque G): la plantilla y cualquier otro Excel pasan por la misma
 * revisión de «Antes de guardar» (`buildGastosPlantilla` → `revisarGastos`).
 */
